import React, { useState, useEffect } from 'react'
import { ActiveCounterChart } from './ActiveCounterChart'
import { Services } from '@/services/services'
import { CurrentSession } from '@/services/repository/session'
import { useTranslations } from '@/hooks/useTranslations'
import { SearchCriteria, SearchCriteriaParams } from '@/pages/communal/statistics/components/common/searchCriteria'
import { mapBuckets } from '../common/labels'
import { HelpPopupButton } from '@/popups/helppopup/help'
import { useModal } from '@/components/modal/Modal'
import { ActiveCounterPopupBody } from './ActiveCounterPopup'

type ActiveCounterBoxProps = { 
  services: Services
  session: CurrentSession
  criteria: SearchCriteria
  goToMachinePage: (machineId: string) => void
}

// Interface representing a bucket data entry from response
export interface ActiveCounterBucket {
  bucket: string       // ISO timestamp string (hour)
  activeCount: number  // number of active devices
}

// Interface representing the full API response (object with buckets array)
export interface ActiveCounterResponse {
  buckets: ActiveCounterBucket[]
  forTime: string    // ISO time of aggregation
}

export const ActiveCounterBox = ({ services, session, criteria, goToMachinePage }: ActiveCounterBoxProps) => {
  const translates = useTranslations(services, session)
  const { openModal } = useModal()
  const [response, setResponse] = useState<ActiveCounterResponse>()

  useEffect(() => {
    setResponse(undefined)
    services.session
      .fetchWithToken(`/api/communal/statistics/machines/machine/activecounter?${SearchCriteriaParams(criteria)}`)
      .then((r) => r.json())
      .then((data) => {
        setResponse(data)
      })
  }, [services.session, criteria])

  if (translates === undefined || !response) {
    return <div className='spinner'></div>
  }

  // Prepare labels and data for the chart
  const labels = response.buckets.map(mapBuckets)
  const activeCounts = response.buckets.map(row => row.activeCount)

  return (
    <div>
      <HelpPopupButton helpKey='page.dashboard.global.box.machine_activecounter.help' translates={translates}/>
      <ActiveCounterChart
        labels={labels}
        dataSet={activeCounts}
        chartTitle={translates.for('page.dashboard.global.box.machine_activecounter.title')}
        dataLabel={translates.for('page.dashboard.global.box.machine_activecounter.data_label')}
        onClick={(i) => openModal((props) => ActiveCounterPopupBody({
          ...props,
          services,
          session,
          criteria,
          bucketFor: new Date(response.buckets[i].bucket),
          goToMachinePage
        }))}
      />
    </div>
  )
}
